import React from 'react'
import styled, { css } from 'styled-components'

import IconButton from 'components/lib/DesignSystem/V3/IconButton'

import CollapseToggle from '../sideNav/CollapseToggle'

import NotificationsMenu from './NotificationsMenu'
import ProfileMenu from './ProfileMenu'
import TopBarCompanySelector from './TopBarCompanySelector'

/** Hamburger icon — inline to avoid extra asset dep */
const HamburgerIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
)

// The new platform top bar sits over the content column only (the logo lives
// in the SideNav header). Design review (2026-07-27): at rest it is fully
// transparent — content scrolls underneath it with no seam — and only once
// the content scroller has moved past the top does it become "one piece": a
// solid surface fill + a bottom divider, no in-between states. The border is
// always 1px (color only toggles) so the switch never shifts layout. The
// company selector / bell / hamburger read --top-navigation-text; we re-point
// that (and the hover bg) here so we don't touch the legacy navy
// --top-navigation-* globals. Hover matches the rail (#eaefff).
const Bar = styled.header`
  display: flex;
  align-items: center;
  /* Single source of truth: PlatformNavLayout publishes --top-bar-height, and
     the rail offsets its scrolled-state divider by the same value. */
  height: var(--top-bar-height, 52px);
  padding: 0 16px 0 10px;
  gap: 8px;
  background: transparent;
  border-bottom: 1px solid transparent;
  position: sticky;
  top: 0;
  z-index: var(--z-index-navigation, 100);
  user-select: none;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;

  --top-navigation-text: var(--color-text);
  /* Same wash as the rail / collapse toggle (Figma Blue/100 #eaefff), not
     --color-elevated-mark (gray). Company selector + bell inherit this. */
  --top-navigation-item-hover-bg: var(--sidebar-item-hover-background);

  ${({ $scrolled }) =>
    $scrolled &&
    css`
      background: var(--color-elevated-solid);
      border-bottom-color: var(--color-border);
    `}
`

const HamburgerWrapper = styled.div`
  display: none;
  margin-right: 4px;

  /* Show below the 1050px mobile breakpoint (§6.1 / AC12) */
  @media (max-width: 1050px) {
    display: inline-flex;
  }

  > button {
    color: var(--color-text);
  }

  > button:not(:disabled):hover,
  > button:not(:disabled):active {
    background-color: var(--sidebar-item-hover-background);
    color: var(--color-text);
  }
`

// Desktop-only collapse/expand toggle, pinned to the top bar's left edge so it
// always sits just outside the rail (Figma "Close nav -Max features": the
// sidebar toggle lives at the top of the content column next to the rail, not
// stacked inside the rail header). Hidden ≤1050px, where the hamburger owns the
// off-canvas drawer instead.
const CollapseToggleWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  /* 10px from the rail edge (Bar padding-left). No extra left margin — that
     used to stack to 24px and float the glyph too far into the page. */
  margin-right: 4px;
  /* Vertically center the toggle on the rail's company logo, NOT on the top
     bar's mid-line. The collapsed rail header (SideNav) offsets its 40px logo by
     a 10px top padding (logo center = 30px), while the top bar would center a
     shorter 32px button at 26px. Reproduce the logo's band here — 40px tall,
     pushed down 10px, toggle centered within it — so the two stay level even if
     the top-bar height or the button size changes. */
  align-self: flex-start;
  height: 40px;
  margin-top: 10px;

  @media (max-width: 1050px) {
    display: none;
  }
`

// Pushes the action cluster to the right edge (the logo lives in the SideNav
// header; the bar's left side carries only the collapse toggle on desktop).
const Spacer = styled.div`
  flex: 1;
  min-width: 0;
`

/**
 * RightActions — company selector + notifications + profile, grouped at the
 * right edge of the bar (Figma 1:26644).
 */
const RightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`

/**
 * TopBar — slim sticky top bar over the content column.
 *
 * Left:   collapse toggle (desktop) / hamburger (mobile ≤1050px, calls onToggleMobileNav)
 * Right:  TopBarCompanySelector (MSP/partner only) + NotificationsMenu + ProfileMenu
 *
 * The IRONSCALES logo lives in the SideNav header. The close-nav control
 * lives in that header while the rail is open; this bar only shows the
 * expand control when the rail is collapsed.
 *
 * Props
 * -----
 * onToggleMobileNav  {() => void}  Opens the off-canvas SideNav drawer (mobile).
 * collapsed          {boolean}     Current rail collapsed state (desktop toggle).
 * onToggleCollapsed  {() => void}  Toggles the rail collapsed state (desktop).
 * collapseDisabled   {boolean}     Disable the toggle (Settings panel is open).
 * scrolled           {boolean}     Whether the content scroller has moved past
 *                                  the top — switches the bar to its solid
 *                                  fill + divider (design review, 2026-07-27).
 * className          {string}      Optional styled-components className.
 */
const TopBar = ({
  onToggleMobileNav,
  collapsed,
  onToggleCollapsed,
  collapseDisabled,
  scrolled,
  className,
}) => (
  <Bar id="top-bar" className={className} role="banner" $scrolled={scrolled}>
    {/* Hamburger — visible only below 1050px; opens the off-canvas drawer */}
    <HamburgerWrapper>
      <IconButton
        id="top-bar-hamburger"
        dataPendoFeature="nav-mobile-open"
        ariaLabel="Open navigation"
        size="medium"
        onClick={onToggleMobileNav}
      >
        <HamburgerIcon />
      </IconButton>
    </HamburgerWrapper>

    {/* Expand control — desktop only, and only while the rail is collapsed.
        The close control sits inside the open SideNav header. Hidden ≤1050px,
        where the hamburger owns the off-canvas drawer instead. */}
    {onToggleCollapsed && collapsed && (
      <CollapseToggleWrapper>
        <CollapseToggle
          collapsed={collapsed}
          onToggle={onToggleCollapsed}
          disabled={collapseDisabled}
          dataPendoFeature="nav-rail-expand"
        />
      </CollapseToggleWrapper>
    )}

    <Spacer />

    {/* Right-side actions — company selector (MSP only) + notifications + profile */}
    <RightActions>
      <TopBarCompanySelector />
      <NotificationsMenu />
      <ProfileMenu />
    </RightActions>
  </Bar>
)

export default TopBar
